import React, { useState, useContext } from "react";
import { ThemeContext } from "../theme";
import { LanguageContext } from "../LanguageContext";
import AlertModal from "../../components/AlertModal";

import text from "./utils/reportsTranslations";
import { getResponsiveStyles } from "./utils/reportsStyles";
import { translateText } from "./utils/reportsHelpers";
import { getCategories } from "./constants/reportsCategories";

import useScreenSize from "./hooks/useScreenSize";
import useReportsData from "./hooks/useReportsData";
import useEmailModal from "./hooks/useEmailModal";

import StatusAlert from "./components/StatusAlert";
import EnhancedEmailModal from "./components/EnhancedEmailModal";
import ReportsControlPanel from "./components/ReportsControlPanel";
import ReportCard from "./components/ReportCard";

const Reports = () => {
  const { theme } = useContext(ThemeContext);
  const { language } = useContext(LanguageContext);

  // local state
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const sortBy = "newest";
  const viewMode = "grid";
  const [alertModal, setAlertModal] = useState({ open: false, title: "", message: "", variant: "info" });

  // hooks
  const { screenSize } = useScreenSize();
  const { reportsData, currentUser, handleDownloadReport } = useReportsData(language);
  const { showEmailModal, setShowEmailModal, selectedReport, setSelectedReport, emailLoading, emailStatus, setEmailStatus, emailForm, setEmailForm, downloadFormat, setDownloadFormat, handleShareReport } = useEmailModal(language, currentUser);

  // utils
  const styles = getResponsiveStyles(theme, language, screenSize, viewMode, emailLoading, emailStatus);
  const categories = getCategories(text, language);



  const closeAlert = () => setAlertModal(prev => ({ ...prev, open: false }));

  const filteredReports = reportsData
    .filter(report => {
      const matchesCategory = selectedCategory === "all" || report.category === selectedCategory;
      const matchesSearch =
        report.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        report.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        report.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
      return matchesCategory && matchesSearch;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case "newest": return new Date(b.lastGenerated) - new Date(a.lastGenerated);
        case "oldest": return new Date(a.lastGenerated) - new Date(b.lastGenerated);
        case "downloads": return b.downloads - a.downloads;
        case "rating": return b.rating - a.rating;
        default: return 0;
      }
    });

  return (
    <div>
      <StatusAlert
        emailStatus={emailStatus}
        onClose={() => setEmailStatus({ type: "", message: "" })}
        styles={styles}
        screenSize={screenSize}
      />

      <EnhancedEmailModal
        showEmailModal={showEmailModal}
        selectedReport={selectedReport}
        emailForm={emailForm}
        setEmailForm={setEmailForm}
        emailLoading={emailLoading}
        onClose={() => setShowEmailModal(false)}
        handleShareReport={handleShareReport}
        styles={styles}
        screenSize={screenSize}
        language={language}
        text={text}
        translateText={(t, cat) => translateText(t, cat, language, text)}
      />

      <div style={styles.headerSection}>
        <div style={styles.headerContent}>
          <div style={styles.headerLeft}>
            <div style={styles.headerText}>
              <h1 style={styles.headerTitle}>{text[language].title}</h1>
            </div>
          </div>
        </div>
      </div>

      <ReportsControlPanel
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        categories={categories}
        selectedCategory={selectedCategory}
        setSelectedCategory={setSelectedCategory}
        styles={styles}
        text={text}
        language={language}
        screenSize={screenSize}
      />

      <div style={styles.reportsGrid}>
        {filteredReports.map(report => (
          <ReportCard
            key={report.id}
            report={report}
            language={language}
            theme={theme}
            screenSize={screenSize}
            styles={styles}
            downloadFormat={downloadFormat}
            setDownloadFormat={setDownloadFormat}
            handleDownloadReport={handleDownloadReport}
            onShare={() => {
              setSelectedReport(report);
              setEmailForm({
                recipients: [],
                subject: `${text[language].shareReport}: ${report.title}`,
                message: text[language].addPersonalMessage,
                includeAttachment: true,
                format: "pdf"
              });
              setShowEmailModal(true);
            }}
            translateText={(t, cat) => translateText(t, cat, language, text)}
            text={text}
          />
        ))}
      </div>
      <style jsx>{`
  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateX(${language === 'ar' ? '-' : ''}100px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
  
  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  .animate-spin {
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }

  ::-webkit-scrollbar {
    width: clamp(6px, 1vw, 8px);
    height: clamp(6px, 1vw, 8px);
  }
  
  ::-webkit-scrollbar-track {
    background: ${theme === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"};
    border-radius: 10px;
  }
  
  ::-webkit-scrollbar-thumb {
    background: ${theme === "dark" ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.2)"};
    border-radius: 10px;
    transition: all 0.3s ease;
  }
  
  ::-webkit-scrollbar-thumb:hover {
    background: ${theme === "dark" ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.3)"};
  }

  .categories-grid::-webkit-scrollbar { display: none !important; }
  .categories-grid { scrollbar-width: none !important; -ms-overflow-style: none !important; }

  @media (max-width: 768px) {
    .control-row { flex-direction: column !important; align-items: stretch !important; }
    .categories-grid {
      overflow-x: auto !important;
      -webkit-overflow-scrolling: touch !important;
      scroll-snap-type: x mandatory !important;
      display: flex !important;
      gap: 12px !important;
      padding-bottom: 8px !important;
    }
    .categories-grid > * { scroll-snap-align: start !important; flex-shrink: 0 !important; min-width: 140px !important; }
    .modal-actions { flex-direction: column !important; gap: 8px !important; }
    .modal-actions button { width: 100% !important; justify-content: center !important; }
  }

  @media (max-width: 480px) {
    .action-buttons {
      display: flex !important;
      gap: 8px !important;
      align-items: center !important;
      justify-content: flex-end !important;
      overflow-x: auto !important;
      flex-wrap: nowrap !important;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    * { animation-duration: 0.01ms !important; transition-duration: 0.01ms !important; }
  }

  @media (max-width: 768px) {
    button { min-height: 44px !important; min-width: 44px !important; }
  }

  * { -webkit-tap-highlight-color: transparent; }
`}</style>

      <AlertModal
        open={alertModal.open}
        title={alertModal.title}
        message={alertModal.message}
        variant={alertModal.variant}
        onClose={closeAlert}
        theme={theme === "dark" ? "dark" : "light"}
        language={language}
      />
    </div>
  );
};

export default Reports;